import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DownOutlined, HeartOutlined, ShoppingOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Dropdown, Image, Space, Typography, Empty, Badge, List, Divider } from 'antd';

import Logo from "assets/images/Logo.svg";
import { useAuthContext } from 'contexts/AuthContext';
import { categories as dummyCategories } from 'data/dummydata';
import { useWishlistContext } from 'contexts/WishlistContext';
import { useCartContext } from 'contexts/CartContext';

const { Text } = Typography;

const ShopDropdownMenu = ({ categories }) => {
    const categoryItems = Object.values(categories).map((category, index) => ({
        key: category.slug,
        label: (
            <Link to={`/products?category=${encodeURIComponent(category.slug)}`} className=' text-decoration-none'>
                {category.name}
            </Link>
        ),
        column: Math.floor(index / 5)
    }));

    return (
        <div className="bg-light header-cards shadow p-3">
            <List grid={{ column: Math.ceil(categoryItems.length / 5) }} dataSource={categoryItems} renderItem={(item) => (
                <List.Item>
                    {item.label}
                </List.Item>
            )}
            />
        </div>
    );
};

const WishlistDropdown = ({ wishlistItems, removeFromWishlist }) => (
    <div className='bg-light header-cards shadow'>
        {wishlistItems.length > 0 ? (
            <List itemLayout="horizontal" dataSource={wishlistItems}
                renderItem={item => (
                    <List.Item actions={[<DeleteOutlined style={{ color: 'red' }} onClick={() => removeFromWishlist(item.id)} />]}>
                        <List.Item.Meta
                            className='d-flex justify-content-center align-items-center'
                            avatar={<Image src={item.images[0]} shape="square" size="large" preview={false} height={100} width={100} className='object-fit-cover rounded-2' />}
                            title={<Link to={`/product?category=${encodeURIComponent(item.slug)}`} className='text-decoration-none fs-6'>{item.name}</Link>}
                            description={<div>${item.price.toFixed(2)}</div>}
                        />
                    </List.Item>
                )}
            />
        ) : (
            <Empty description="No items in wishlist" />
        )}
        <Divider />
        <Link to="/wish-list" className='text-decoration-none'>
            <button className='w-100 btn btn-outline-primary'>
                Wish List
            </button>
        </Link>
    </div>
);

const CartDropdown = ({ cartItems, removeFromCart }) => (
    <div className='bg-light header-cards shadow'>
        {cartItems.length > 0 ? (
            <List itemLayout="horizontal" dataSource={cartItems}
                renderItem={item => (
                    <List.Item actions={[<DeleteOutlined style={{ color: 'red' }} onClick={() => removeFromCart(item.id)} />]}>
                        <List.Item.Meta
                            className='d-flex justify-content-center align-items-center'
                            avatar={<Image src={item.images[0]} shape="square" size="large" preview={false} height={100} width={100} className='object-fit-cover rounded-2' />}
                            title={<Link to={`/product?category=${encodeURIComponent(item.slug)}`} className='text-decoration-none fs-6'>{item.name}</Link>}
                            description={
                                <>
                                    <div>${item.price.toFixed(2)} x {item.quantity}</div>
                                    <div>Size: {item.size}</div>
                                </>
                            }
                        />
                    </List.Item>
                )}
            />
        ) : (
            <Empty description="No items in cart" />
        )}
        {cartItems.length > 0 && (
            <div className='mt-3 fw-bold fs-6 d-flex justify-content-between'>
                <span>SubTotal: </span>
                ${cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0).toFixed(2)}
            </div>
        )}
        <Divider />
        <Space className='w-100' direction='vertical'>
            <Link to="/cart" className='text-decoration-none'>
                <button className='w-100 btn btn-outline-primary'>
                    View Cart
                </button>
            </Link>
            <Link to="/checkout" className='text-decoration-none mt-4'>
                <button className='w-100 btn btn-primary'>
                    Checkout
                </button>
            </Link>
        </Space>
    </div>
);

export default function Header() {
    const { isAuthenticated } = useAuthContext();
    const { wishlistItems, removeFromWishlist } = useWishlistContext();
    const { cartItems, removeFromCart } = useCartContext();
    const [isWishlistDropdownVisible, setWishlistDropdownVisible] = useState(false);
    const [isCartDropdownVisible, setCartDropdownVisible] = useState(false);
    const [isShopDropdownVisible, setShopDropdownVisible] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 992) {
                setWishlistDropdownVisible(false);
                setCartDropdownVisible(false);
                setShopDropdownVisible(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <nav className="navbar navbar-expand-lg bg-body-tertiary shadow sticky-top">
            <div className="container-fluid d-flex justify-content-between align-items-center">
                <Link className="navbar-brand" to={"/"}>
                    <Image src={Logo} preview={false} height={40} />
                </Link>

                <button className="navbar-toggler border-0" type="button" data-bs-toggle="offcanvas" data-bs-target="#offcanvasNavbar" aria-controls="offcanvasNavbar">
                    <span className="navbar-toggler-icon"></span>
                </button>

                <div className="collapse navbar-collapse justify-content-center" id="navbarNav">
                    <ul className="navbar-nav">
                        <li className="nav-item">
                            <Link className="nav-link active" to="/">Home</Link>
                        </li>
                        <li className="nav-item">
                            <Dropdown
                                trigger={['click']}
                                open={isShopDropdownVisible}
                                onOpenChange={(visible) => setShopDropdownVisible(visible)}
                                dropdownRender={() => <ShopDropdownMenu categories={dummyCategories} />}
                            >
                                <Link className="nav-link">Shop<DownOutlined className='ms-1' /></Link>
                            </Dropdown>
                        </li>
                        <li className="nav-item"><Link className="nav-link" to="/">Our Story</Link></li>
                        <li className="nav-item"><Link className="nav-link" to="/">Blog</Link></li>
                        <li className="nav-item"><Link className="nav-link" to="/">Contact Us</Link></li>
                    </ul>
                </div>

                <div className="offcanvas offcanvas-start d-block d-lg-none" tabIndex="-1" id="offcanvasNavbar" aria-labelledby="offcanvasNavbarLabel">
                    <div className="offcanvas-header">
                        <h5 className="offcanvas-title" id="offcanvasNavbarLabel">Menu</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                    </div>
                    <div className="offcanvas-body">
                        <ul className="navbar-nav justify-content-end flex-grow-1 pe-3">
                            <li className="nav-item"><Link className="nav-link" to="/">Home</Link></li>
                            <li className="nav-item">
                                <Dropdown
                                    trigger={['click']}
                                    onOpenChange={(visible) => setShopDropdownVisible(visible)}
                                    dropdownRender={() => <ShopDropdownMenu categories={dummyCategories} />}
                                >
                                    <Link className="nav-link">
                                        <Space>
                                            Shop
                                            <DownOutlined />
                                        </Space>
                                    </Link>
                                </Dropdown>
                            </li>
                            <li className="nav-item"><Link className="nav-link" to="/">Our Story</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/">Blog</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/">Contact Us</Link></li>
                            {isAuthenticated && <>
                                <li className="nav-item"><Link className="nav-link fs-4" to="/wish-list"><HeartOutlined /></Link></li>
                                <li className="nav-item"><Link className="nav-link fs-4" to="/cart"><ShoppingOutlined /></Link></li>
                            </>}
                        </ul>
                        <div className="d-flex d-lg-none align-items-center gap-4">
                            {!isAuthenticated ? (
                                <Button type='primary'>
                                    <Link to={"/auth/login"} className='text-decoration-none'>Login</Link>
                                </Button>
                            ) : (
                                <Button type='primary'>
                                    <Link to={"/dashboard"} className='text-decoration-none'>Dashboard</Link>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="d-none d-lg-flex align-items-center gap-4">
                    {!isAuthenticated ? (
                        <Button type='primary'>
                            <Link to={"/auth/login"} className='text-decoration-none'>Login</Link>
                        </Button>
                    ) : (
                        <>
                            <Dropdown trigger={['click']} placement="bottomLeft" dropdownRender={() => <CartDropdown cartItems={cartItems} removeFromCart={removeFromCart} />} open={isCartDropdownVisible} onOpenChange={(visible) => setCartDropdownVisible(visible)}>
                                <Badge count={cartItems.reduce((acc, item) => acc + item.quantity, 0)} showZero style={{ marginTop: "8px" }}>
                                    <Text className='fs-4' style={{ cursor: "pointer" }}><ShoppingOutlined /></Text>
                                </Badge>
                            </Dropdown>
                            <Dropdown trigger={['click']} placement="bottomLeft" dropdownRender={() => <WishlistDropdown wishlistItems={wishlistItems} removeFromWishlist={removeFromWishlist} />} open={isWishlistDropdownVisible} onOpenChange={(visible) => setWishlistDropdownVisible(visible)}>
                                <Badge count={wishlistItems.length} showZero style={{ marginTop: "8px" }}>
                                    <Text className='fs-4' style={{ cursor: "pointer" }}><HeartOutlined /></Text>
                                </Badge>
                            </Dropdown>
                            <Button type='primary'>
                                <Link to={"/dashboard"} className='text-decoration-none'>Dashboard</Link>
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </nav>
    );
}